import type { StatusTone } from '@noor/ui';
import type { CourierActivity, CourierStatusFilter } from '../model/schema';

/**
 * Static registries for the couriers list (ported from cpanel
 * `assets/data/courier-stasuses.js` + `FilterMenu.jsx`). Labels resolve
 * through i18n at render time; only ids/keys live here.
 */

export interface CourierStatusDef {
  id: CourierStatusFilter;
  /** i18n key under `courier:list.status.*`. */
  labelKey: string;
  /** Key of the badge count inside the list response `stats`. */
  countKey: string;
  tone: StatusTone;
  /** Optional hint key shown on the tab (cpanel `tooltip`). */
  tooltipKey?: string;
}

export const COURIER_STATUSES: readonly CourierStatusDef[] = [
  {
    id: 'ALL',
    labelKey: 'courier:list.status.ALL',
    countKey: 'total_count',
    tone: 'neutral',
  },
  {
    id: 'ACTIVE',
    labelKey: 'courier:list.status.ACTIVE',
    countKey: 'active_count',
    tone: 'active',
    tooltipKey: 'courier:list.statusHint.ACTIVE',
  },
  {
    id: 'INACTIVE',
    labelKey: 'courier:list.status.INACTIVE',
    countKey: 'inactive_count',
    tone: 'danger',
  },
  {
    id: 'NEED_VISIT',
    labelKey: 'courier:list.status.NEED_VISIT',
    countKey: 'needs_visit_count',
    tone: 'pending',
  },
  {
    id: 'BLOCKED',
    labelKey: 'courier:list.status.BLOCKED',
    countKey: 'blocked_count',
    tone: 'danger',
  },
  {
    id: 'UNACTIVATED',
    labelKey: 'courier:list.status.UNACTIVATED',
    countKey: 'unactivated_count',
    tone: 'success',
  },
  {
    id: 'FIRED',
    labelKey: 'courier:list.status.FIRED',
    countKey: 'fired_count',
    tone: 'danger',
    tooltipKey: 'courier:list.statusHint.FIRED',
  },
] as const;

/**
 * Cell status lookup (cpanel `getCourierStatus`): an unknown key falls back to
 * the LAST registry entry, exactly like the legacy `findIndex === -1` branch.
 */
export function getCourierStatusDef(key: string | null | undefined): CourierStatusDef {
  const found = COURIER_STATUSES.find((status) => status.id === key);
  return found ?? COURIER_STATUSES[COURIER_STATUSES.length - 1]!;
}

export const COURIER_ACTIVITY_OPTIONS: readonly {
  value: CourierActivity;
  labelKey: string;
}[] = [
  { value: 'has_activity', labelKey: 'courier:list.activity.has_activity' },
  { value: 'has_scooter', labelKey: 'courier:list.activity.has_scooter' },
  { value: 'no_activity', labelKey: 'courier:list.activity.no_activity' },
  { value: 'no_scooter', labelKey: 'courier:list.activity.no_scooter' },
] as const;

/** Fields the grid can sort (cpanel `CustomHeader` cycle, single active). */
export const COURIER_SORT_FIELDS = [
  'created_date',
  'status',
  'last_order_date',
  'average_cheque',
  'orders_count',
  'average_rating',
  'balance',
  'scooter_debt',
] as const;
export type CourierSortField = (typeof COURIER_SORT_FIELDS)[number];

/** Requirement flags counted by the `x / 5` cell, in legacy order. */
export const COURIER_REQUIREMENT_KEYS = [
  'employment_status_set',
  'commissioner_is_set',
  'visited_office',
  'passed_test',
  'driving_license_approved',
] as const;

/** The legacy "no date" sentinel the cash-balance cell treats as missing. */
export const ZERO_DATE = '0001-01-01T00:00:00Z';

/** Largest amount the limit / bonus / foul inputs accept (legacy validators). */
export const MAX_COURIER_AMOUNT = 1_000_000;

/** Legacy bonus-button quirk: outside prod it shows for all, in prod for one user. */
export const COURIER_BONUS_PROD_USER_ID = 83;
